import { useState, useEffect } from "react";

import { Edit, Archive, Trash2, Eye, FileText, Sparkles, RefreshCw } from "lucide-react";

import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Markdown } from "@/components/ui/markdown";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { Textarea } from "@/components/ui/textarea";
import { useAdminEdgeFunction } from "@/hooks/useAdminEdgeFunction";
import { useCrud } from "@/hooks/useCrud";
import { supabase } from "@/integrations/supabase/client";

import { CrudCard } from "./CrudCard";
import { CrudManager } from "./CrudManager";
import { CrudItemBase, CrudStats } from "./types/crud";


interface SessionFramework extends CrudItemBase {
  content: string;
  category: string;
  parent_framework_id: string | null;
}

interface UsageStats {
  id: string;
  name: string;
  total_uses: number;
  unique_projects: number;
  uses_last_7_days: number;
  uses_last_30_days: number;
  last_used_at: string | null;
}

export const SessionFrameworksManager = () => {
  const { invokeAdmin, isLoading: isGenerating, isAdmin } = useAdminEdgeFunction();
  const {
    items: frameworks,
    isLoading: loading,
    createItem: createFramework,
    updateItem: updateFramework,
    deleteItem: deleteFramework,
  } = useCrud<SessionFramework>({
    tableName: "session_frameworks",
    queryKey: ["session_frameworks"],
    order: { column: "created_at", options: { ascending: false } },
  });
  const [usageStats, setUsageStats] = useState<UsageStats[]>([]);
  const [editingFramework, setEditingFramework] = useState<SessionFramework | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [previewFramework, setPreviewFramework] = useState<SessionFramework | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [deleteFrameworkId, setDeleteFrameworkId] = useState<string | null>(null);
  const [archiveFrameworkId, setArchiveFrameworkId] = useState<string | null>(null);
  const [showStats, setShowStats] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content: '',
    category: 'general',
    status: 'draft' as 'draft' | 'published' | 'archived'
  });

  useEffect(() => {
    fetchUsageStats();
  }, []);

  const fetchUsageStats = async () => {
    try {
      const { data, error } = await supabase
        .from('session_framework_usage_stats')
        .select('*')
        .order('total_uses', { ascending: false });

      if (error) throw error;
      setUsageStats(data || []);
    } catch (error: any) {
      console.error('Failed to fetch usage stats:', error);
    }
  };

  const handleSaveFramework = async () => {
    if (!formData.name || !formData.description || !formData.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (editingFramework) {
      await updateFramework({ id: editingFramework.id, updates: formData });
    } else {
      await createFramework(formData as any);
    }

    setIsDialogOpen(false);
    resetForm();
    fetchUsageStats();
  };

  const handlePublish = async (id: string) => {
    await updateFramework({ id, updates: { status: 'published', updated_at: new Date().toISOString() } });
  };

  const handleArchive = async () => {
    if (!archiveFrameworkId) return;

    await updateFramework({
      id: archiveFrameworkId,
      updates: {
        status: 'archived',
        updated_at: new Date().toISOString(),
      },
    });
    setArchiveFrameworkId(null);
  };

  const handleDelete = async () => {
    if (!deleteFrameworkId) return;

    await deleteFramework(deleteFrameworkId);
    setDeleteFrameworkId(null);
    fetchUsageStats();
  };

  const handleCreateVersion = async (frameworkId: string) => {
    try {
      const framework = frameworks.find(f => f.id === frameworkId);
      if (!framework) return;

      const { data, error } = await supabase.rpc('create_framework_version', {
        framework_id: frameworkId,
        new_name: framework.name,
        new_description: framework.description,
        new_content: framework.content,
        new_category: framework.category
      });

      if (error) throw error;
      toast.success('New version created successfully');
    } catch (error: any) {
      toast.error('Failed to create version: ' + error.message);
    }
  };

  const openEditDialog = (framework: SessionFramework) => {
    setEditingFramework(framework);
    setFormData({
      name: framework.name,
      description: framework.description,
      content: framework.content,
      category: framework.category,
      status: framework.status
    });
    setIsDialogOpen(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const handleGenerateContent = async (mode: 'generate' | 'enhance') => {
    if (!formData.name || !formData.description || !formData.category) {
      toast.error('Please fill in name, description, and category first');
      return;
    }

    if (mode === 'enhance' && !formData.content) {
      toast.error('No content to enhance');
      return;
    }

    const { data, error } = await invokeAdmin('generate-framework-content', {
      body: {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        existingContent: formData.content,
        mode
      },
      showErrorToast: true,
    });

    if (error) return;

    if (data?.error) {
      toast.error(data.error);
      return;
    }

    if (data?.content) {
      setFormData({ ...formData, content: data.content });
      toast.success(mode === 'generate' ? 'Content generated successfully' : 'Content enhanced successfully');
    }
  };

  const resetForm = () => {
    setEditingFramework(null);
    setFormData({
      name: '',
      description: '',
      content: '',
      category: 'general',
      status: 'draft'
    });
  };

  const getUsageForFramework = (frameworkId: string) => {
    return usageStats.find(stat => stat.id === frameworkId);
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <>
      <CrudManager
        title="Session Frameworks Manager"
        description="Manage reusable session templates and frameworks"
        onCreateNew={openCreateDialog}
        createLabel="Create Framework"
        showStats={showStats}
        onToggleStats={() => setShowStats(!showStats)}
        statsSection={
          <div className="space-y-2">
            {usageStats.slice(0, 10).map(stat => (
              <div key={stat.id} className="flex justify-between items-center p-2 border rounded">
                <span className="font-medium">{stat.name}</span>
                <div className="flex gap-4 text-sm text-muted-foreground">
                  <span>Total: {stat.total_uses}</span>
                  <span>7d: {stat.uses_last_7_days}</span>
                  <span>30d: {stat.uses_last_30_days}</span>
                  <span>Projects: {stat.unique_projects}</span>
                </div>
              </div>
            ))}
          </div>
        }
      >
        {frameworks.map((framework) => {
          const usage = getUsageForFramework(framework.id);
          return (
            <CrudCard
              key={framework.id}
              item={framework}
              stats={usage ? {
                total_uses: usage.total_uses,
                unique_users: usage.unique_projects,
                uses_last_7_days: usage.uses_last_7_days,
              } : undefined}
              additionalBadges={
                <Badge variant="outline">{framework.category}</Badge>
              }
              actions={[
                {
                  label: '',
                  icon: Eye,
                  onClick: () => {
                    setPreviewFramework(framework);
                    setIsPreviewOpen(true);
                  },
                  variant: 'ghost',
                  title: 'Preview',
                },
                {
                  label: '',
                  icon: FileText,
                  onClick: () => handleCreateVersion(framework.id),
                  variant: 'ghost',
                  title: 'Create new version',
                  hidden: framework.status !== 'published',
                },
                {
                  label: 'Publish',
                  icon: () => null as any,
                  onClick: () => handlePublish(framework.id),
                  variant: 'ghost',
                  hidden: framework.status !== 'draft',
                },
                {
                  label: '',
                  icon: Edit,
                  onClick: () => openEditDialog(framework),
                  variant: 'ghost',
                  title: 'Edit',
                },
                {
                  label: '',
                  icon: Archive,
                  onClick: () => setArchiveFrameworkId(framework.id),
                  variant: 'ghost',
                  title: 'Archive',
                },
                {
                  label: '',
                  icon: Trash2,
                  onClick: () => setDeleteFrameworkId(framework.id),
                  variant: 'ghost',
                  title: 'Delete',
                },
              ]}
            />
          );
        })}
      </CrudManager>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingFramework ? 'Edit Framework' : 'Create Framework'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Framework name"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Category</label>
              <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="brainstorming">Brainstorming</SelectItem>
                  <SelectItem value="decision-making">Decision Making</SelectItem>
                  <SelectItem value="problem-solving">Problem Solving</SelectItem>
                  <SelectItem value="strategy">Strategy</SelectItem>
                  <SelectItem value="review">Review</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Framework description"
                rows={3}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Content (Markdown)</label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleGenerateContent('generate')}
                    disabled={isGenerating || !formData.name || !formData.description}
                  >
                    <Sparkles className="h-3 w-3 mr-1" />
                    {isGenerating ? 'Generating...' : 'Generate with AI'}
                  </Button>
                  {formData.content && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleGenerateContent('enhance')}
                      disabled={isGenerating}
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      {isGenerating ? 'Enhancing...' : 'Enhance with AI'}
                    </Button>
                  )}
                </div>
              </div>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Framework content in markdown format (or use AI to generate)"
                rows={10}
                className="font-mono text-sm"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Status</label>
              <Select value={formData.status} onValueChange={(value: any) => setFormData({ ...formData, status: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveFramework}>
                {editingFramework ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewFramework?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground">{previewFramework?.description}</p>
            <div className="border rounded-lg p-4">
              <Markdown content={previewFramework?.content || ''} />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!archiveFrameworkId} onOpenChange={() => setArchiveFrameworkId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Framework</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive this framework? It will no longer be visible to users.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive}>Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteFrameworkId} onOpenChange={() => setDeleteFrameworkId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Framework</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this framework? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
